import React from "react";
import { FileText, CheckCircle2, ChevronRight } from "lucide-react";
import { useVisualCopy } from "@/locales/visualCopy";

/**
 * InterfaceMockup — a CSS/SVG-based faux screenshot of the TradeDocs workspace.
 * Replaces generic stock photography: shows Template Library, AI drafting,
 * generated LOI, deal parameters, document status.
 */
export default function InterfaceMockup({ caption }) {
  const v = useVisualCopy();
  const templates = [
    { code: "LOI", cat: "commercial", active: true },
    { code: "ICPO", cat: "commercial" }, { code: "FCO", cat: "commercial" },
    { code: "LC Draft", cat: "tradeFinance" }, { code: "BCL", cat: "tradeFinance" },
    { code: "Recap", cat: "shipping" }, { code: "Laytime", cat: "shipping" }, { code: "KYC", cat: "corporate" },
  ];
  return (
    <div className="relative min-w-0 w-full">
      {/* Ambient glow */}
      <div className="absolute -inset-4 bg-gradient-radial from-[var(--td-gold-muted)] to-transparent blur-2xl opacity-40 pointer-events-none" />

      {/* Window frame */}
      <div className="relative card-surface overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
        {/* Chrome */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--td-border)] bg-[var(--td-surface-2)]">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#3a3f4c]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#3a3f4c]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#3a3f4c]" />
          </div>
          <div className="font-mono text-[8px] sm:text-[10px] text-[var(--td-text-3)] tracking-wider sm:tracking-widest truncate px-2">
            TRADEDOCS · {v("workspace")}
          </div>
          <div className="w-12" />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-[84px_minmax(0,1fr)] sm:grid-cols-[130px_minmax(0,1fr)] md:grid-cols-[180px_minmax(0,1fr)_240px] min-h-[360px] md:min-h-[420px] min-w-0">
          {/* Sidebar: Template Library */}
          <div className="border-r border-[var(--td-border)] py-3 sm:py-4 px-1.5 sm:px-3 bg-[var(--td-surface)] min-w-0">
            <div className="font-mono text-[7px] sm:text-[9px] uppercase tracking-wider sm:tracking-widest text-[var(--td-text-3)] mb-3 px-1 break-words">
              {v("templateLibrary")}
            </div>
            {templates.map((t) => (
              <div
                key={t.code}
                className={`px-1 sm:px-2 py-1.5 mb-0.5 text-[9px] sm:text-[11px] flex items-center gap-1 sm:gap-2 min-w-0 ${
                  t.active
                    ? "bg-[var(--td-bg)] border-l-2 border-[var(--td-gold)] text-[var(--td-gold)]"
                    : "text-[var(--td-text-2)]"
                }`}
              >
                <FileText size={10} />
                <span className="font-mono">{t.code}</span>
              </div>
            ))}
          </div>

          {/* Main: generated LOI */}
          <div className="p-3 sm:p-5 bg-[var(--td-bg)] min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)] mb-1">
                  LOI · v2
                </div>
                <div className="font-serif text-lg text-[var(--td-text)]">{v("letterIntent")}</div>
              </div>
              <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-wider sm:tracking-widest text-[var(--td-text-3)] flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {v("draftReady")}
              </div>
            </div>

            <div className="font-mono text-[8px] sm:text-[10.5px] leading-relaxed text-[var(--td-text-2)] space-y-1.5 break-words">
              <div className="text-[var(--td-text)]">
                <span className="text-[var(--td-gold)]">##</span> {v("letterIntent").toUpperCase()}
              </div>
              <div className="h-px bg-[var(--td-border)] my-2" />
              <div><span className="text-[var(--td-text-3)]">{v("date")}:</span> <span className="text-[var(--td-text)]">15.01.2026</span></div>
              <div><span className="text-[var(--td-text-3)]">{v("from")}:</span> <span className="text-[var(--td-text)]">{v("buyerCompany")}</span></div>
              <div><span className="text-[var(--td-text-3)]">{v("to")}:</span> <span className="text-[var(--td-text)]">{v("sellerCompany")}</span></div>
              <div className="h-2" />
              <div className="text-[var(--td-text)]">
                <span className="text-[var(--td-gold)]">###</span> 1) {v("commodity")}
              </div>
              <div>{v("commodityValue")}</div>
              <div className="h-2" />
              <div className="text-[var(--td-text)]">
                <span className="text-[var(--td-gold)]">###</span> 2) {v("quantity")}
              </div>
              <div>{v("quantityValue")}</div>
              <div className="h-2" />
              <div className="text-[var(--td-text)]">
                <span className="text-[var(--td-gold)]">###</span> 3) {v("incoterms")}
              </div>
              <div>CIF Mombasa (Incoterms® 2020)</div>
            </div>
          </div>

          {/* Right rail: deal parameters + progress */}
          <div className="hidden md:block border-l border-[var(--td-border)] p-4 bg-[var(--td-surface)] space-y-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)] mb-2">
                {v("dealParameters")}
              </div>
              <div className="space-y-1.5">
                {[
                  [v("buyer"), "Meridian Trade LLC"], [v("seller"), "Kirov Oils"],
                  [v("commodity"), v("sunflowerOil")], [v("quantity"), "5,000 MT"],
                  [v("incoterms"), "CIF Mombasa"], [v("payment"), v("paymentValue")],
                ].map(([k, v]) => (
                  <div key={k} className="text-[10px]">
                    <div className="text-[var(--td-text-3)] font-mono">{k}</div>
                    <div className="text-[var(--td-text)] font-mono truncate">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[var(--td-border)] pt-4">
              <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)] mb-2">
                {v("progress")}
              </div>
              <div className="space-y-1.5">
                {[
                  [v("selected"), true], [v("infoAdded"), true], [v("generated"), true],
                  [v("reviewed"), true], [v("exportReady"), false],
                ].map(([label, done], i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] font-mono">
                    {done ? (
                      <CheckCircle2 size={11} className="text-[var(--td-gold)]" />
                    ) : (
                      <div className="w-[11px] h-[11px] rounded-full border border-[var(--td-border)]" />
                    )}
                    <span className={done ? "text-[var(--td-text)]" : "text-[var(--td-text-3)]"}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {caption && (
        <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">
          {caption}
        </div>
      )}
    </div>
  );
}
